import { Check, EyeSlash } from "@gravity-ui/icons";
import { Flex, Icon, Label, Text } from "@gravity-ui/uikit";

import css from "./TemplatePreviewPanel.module.scss";
import { splitMissingVars } from "./TemplatePreviewPanel.utils";
import type { TemplatePreviewFieldResult } from "./TemplatesForm.types";

const renderEmptyValue = (label: string) => (
  <Text variant="body-2" color="hint">
    <i>{label}</i>
  </Text>
);

// Один шаблонизированный результат: Было / Станет + бейджи статуса +
// разноцветные подсказки про used/missing-переменные. Inлайнится в карточку
// превью-сэмпла, отдельной семантики (entry-point, store) не имеет.
export const PreviewFieldResult = ({
  result,
}: {
  result: TemplatePreviewFieldResult | undefined;
}) => {
  if (!result) return null;

  const valueChanged = result.before !== result.after;
  const { scalar: missingScalars, dynamic: missingDynamic } = splitMissingVars(
    result.missingVars,
  );

  return (
    <Flex direction="column" gap={2}>
      <Flex direction="column" gap={1} className={css.valueBlock}>
        <Text variant="caption-2" color="hint">
          Было
        </Text>
        {result.before ? (
          <Text variant="body-2" className={css.beforeValue}>
            {result.before}
          </Text>
        ) : (
          renderEmptyValue("(пусто)")
        )}
      </Flex>
      <Flex
        direction="column"
        gap={1}
        className={`${css.valueBlock} ${css.afterBlock}`}
      >
        <Text variant="caption-2" color="hint">
          Станет
        </Text>
        {result.after ? (
          <Text
            variant="body-2"
            className={`${css.afterValue} ${valueChanged ? css.afterChanged : ""}`}
          >
            {result.after}
          </Text>
        ) : (
          renderEmptyValue("(пусто)")
        )}
      </Flex>

      <Flex gap={1} wrap>
        {result.applied ? (
          <Label
            theme={valueChanged ? "success" : "unknown"}
            icon={<Icon data={Check} size={12} />}
            size="xs"
          >
            {valueChanged ? "Шаблон применён" : "Без изменений"}
          </Label>
        ) : (
          <Label
            theme="unknown"
            icon={<Icon data={EyeSlash} size={12} />}
            size="xs"
          >
            {missingDynamic.length > 0 && missingScalars.length === 0
              ? "У товара нет нужных полей"
              : "Шаблон не применится"}
          </Label>
        )}
        {result.fellBackTo === "fallback" && (
          <Label theme="info" size="xs">
            Fallback
          </Label>
        )}
        {result.truncated && (
          <Label theme="warning" size="xs">
            Обрезано
          </Label>
        )}
      </Flex>

      {result.usedVars.length > 0 && (
        <Flex gap={1} wrap alignItems="center">
          <Text variant="caption-2" color="hint">
            Использовано:
          </Text>
          {result.usedVars.map((v) => (
            <span key={v} className={`${css.varTag} ${css.varTagUsed}`}>
              {`{{${v}}}`}
            </span>
          ))}
        </Flex>
      )}

      {missingScalars.length > 0 && (
        <Flex gap={1} wrap alignItems="center">
          <Text variant="caption-2" color="danger">
            Нет данных:
          </Text>
          {missingScalars.map((v) => (
            <span key={v} className={`${css.varTag} ${css.varTagMissing}`}>
              {`{{${v}}}`}
            </span>
          ))}
        </Flex>
      )}

      {missingDynamic.length > 0 && (
        <Flex direction="column" gap={1}>
          <Flex gap={1} wrap alignItems="center">
            <Text variant="caption-2" color="hint">
              Нет у этого товара:
            </Text>
            {missingDynamic.map((v) => (
              <span key={v} className={`${css.varTag} ${css.varTagDynamic}`}>
                {`{{${v}}}`}
              </span>
            ))}
          </Flex>
          <Text variant="caption-2" color="hint">
            Динамические поля — у каждого товара свой набор. На реальных данных
            значение может быть.
          </Text>
        </Flex>
      )}
    </Flex>
  );
};
